import { CSSObject } from "styled-components";
import { useTheme } from "../../assets/theme/ThemeProvider";
import { IControlStyles, ISearchStyles } from "../../assets/layouts";

const useServiceRequestStyles = () => {
    const theme = useTheme();
    const styles: {
        badgePriority: CSSObject;
        priorityIcon: CSSObject;
        prioritySquare: CSSObject;
        priorityChip: CSSObject;
        priorityHigh: CSSObject;
        priorityMedium: CSSObject;
        priorityLow: CSSObject;
        srStatus: CSSObject;
        statusSquare: CSSObject;
        dotOpen: CSSObject;
        dotProgress: CSSObject;
        dotClosed: CSSObject;
        btnView: CSSObject;
        disabledContainer: CSSObject;
        disabledCard: CSSObject;
        disabledTitle: CSSObject;
        disabledText: CSSObject;
        sectionHeader: CSSObject;
        historyItem: CSSObject;
        historyHeader: CSSObject;
        historyContent: CSSObject;
        attachmentContainer: CSSObject;
        attachmentImage: CSSObject;
        attachmentList: CSSObject;
        attachmentLink: CSSObject;
        actionItem: CSSObject;
        detailMarkerValue: CSSObject;
        locationWithMap: CSSObject;
        locationDetails: CSSObject;
        mapButton: CSSObject;
        mapFrame: CSSObject;
        search: ISearchStyles;
        searchColumn: CSSObject;
        searchControl: CSSObject;
        searchControlStyles: IControlStyles;
    } = {
        badgePriority: {
            display: "inline-flex",
            alignItems: "center",
            gap: theme.spacePx.sp01,
            fontWeight: theme.font.weights.regular,
            fontSize: "0.85rem",
        },
        priorityIcon: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "18px",
            height: "18px",
            borderRadius: theme.spacePx.sp01,
            color: theme.palette.white,
            fontSize: theme.font.sizes.sm,
            fontWeight: theme.font.weights.bold,
        },
        prioritySquare: {
            display: "inline-block",
            width: theme.spacePx.sp03,
            height: theme.spacePx.sp03,
            borderRadius: "20%",
            flexShrink: 0,
        },
        priorityChip: {
            display: "inline",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "74px",
            padding: "6px 12px",
            color: theme.palette.white,
            fontSize: "0.85rem",
            fontWeight: theme.font.weights.semiBold,
            lineHeight: 1,
            textAlign: "center",
        },
        priorityHigh: {
            backgroundColor: theme.palette.danger.main,
        },
        priorityMedium: {
            backgroundColor: theme.palette.accent.light,
        },
        priorityLow: {
            backgroundColor: theme.palette.success.main,
        },
        srStatus: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacePx.sp01,
            fontSize: theme.font.sizes.md,
            fontWeight: theme.font.weights.semiBold,
            color: theme.palette.text.main,
        },
        statusSquare: {
            width: theme.spacePx.sp03,
            height: theme.spacePx.sp03,
            borderRadius: "20%",
        },
        dotOpen: {
            backgroundColor: theme.palette.accent.light,
        },
        dotProgress: {
            backgroundColor: theme.palette.success.main,
        },
        dotClosed: {
            backgroundColor: theme.palette.information.main,
        },
        btnView: {
            color: theme.palette.link.main,
            fontSize: theme.font.sizes.md,
            fontWeight: theme.font.weights.semiBold,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            transition: "color 0.2s",
            "&:hover": {
                textDecoration: "underline",
                color: theme.palette.link.dark,
            },
        },
        disabledContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 100px)",
            padding: theme.spacePx.sp04,
        },
        disabledCard: {
            textAlign: "center",
            padding: theme.spacePx.sp05,
        },
        disabledTitle: {
            fontSize: theme.font.sizes.xxl,
            fontWeight: theme.font.weights.bold,
            color: theme.palette.text.dark,
            marginBottom: theme.spacePx.sp02,
        },
        disabledText: {
            color: theme.palette.text.main,
            fontSize: theme.font.sizes.lg,
        },
        sectionHeader: {
            fontSize: theme.font.sizes.lg,
            fontWeight: theme.font.weights.regular,
            color: theme.palette.text.main,
            borderBottom: `1px solid ${theme.palette.divider.light}`,
            paddingTop: theme.spacePx.sp02,
            paddingBottom: 0,
            marginTop: theme.spacePx.sp03,
            marginBottom: theme.spacePx.sp03,
            display: "flex",
            alignItems: "center",
            gap: theme.spacePx.sp01,
        },
        historyItem: {
            padding: `${theme.spacePx.sp01} 0`,
            marginBottom: theme.spacePx.sp01,
            borderBottom: `1px solid ${theme.palette.divider.light}`,
        },
        historyHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2px",
            fontSize: theme.font.sizes.md,
            color: theme.palette.black,
            fontWeight: theme.font.weights.bold,
            letterSpacing: 0,
        },
        historyContent: {
            fontSize: theme.font.sizes.md,
            color: theme.palette.text.dark,
            fontWeight: theme.font.weights.regular,
            lineHeight: 1.45,
        },
        attachmentContainer: {
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacePx.sp02,
            marginTop: theme.spacePx.sp01,
        },
        attachmentImage: {
            width: "80px",
            height: "80px",
            objectFit: "cover",
            display: "block",
            border: `1px solid ${theme.palette.divider.light}`,
            borderRadius: theme.spacePx.sp01,
        },
        attachmentList: {
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginTop: theme.spacePx.sp01,
        },
        attachmentLink: {
            display: "inline-flex",
            alignItems: "center",
            gap: theme.spacePx.sp01,
            color: theme.palette.link.main,
            textDecoration: "none",
            fontSize: theme.font.sizes.md,
            fontWeight: theme.font.weights.regular,
            lineHeight: 1.45,
        },
        actionItem: {},
        detailMarkerValue: {
            display: "inline-flex",
            alignItems: "center",
            gap: theme.spacePx.sp01,
            color: theme.palette.text.dark,
            fontWeight: theme.font.weights.regular,
            fontSize: theme.font.sizes.md,
            lineHeight: 1.45,
        },
        locationWithMap: {
            display: "flex",
            alignItems: "flex-start",
            gap: theme.spacePx.sp02,
        },
        locationDetails: {
            flex: 1,
        },
        mapButton: {
            width: "36px",
            height: "36px",
            borderRadius: theme.spacePx.sp01,
            border: `1px solid ${theme.palette.divider.light}`,
            backgroundColor: theme.palette.background.main,
            color: theme.palette.link.main,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            marginTop: "2px",
        },
        mapFrame: {
            width: "100%",
            height: "420px",
            border: "none",
            borderRadius: theme.spacePx.sp01,
            display: "block",
        },
        search: {
            searchContainer: {
                alignItems: "flex-start",
                marginBottom: theme.spacePx.sp04,
                paddingLeft: 0,
                paddingRight: 0,
            },
            fieldsContainer: {
                columnGap: theme.spacePx.sp04,
                rowGap: theme.spacePx.sp03,
            },
            actionsContainer: {
                alignItems: "center",
                alignSelf: "flex-start",
                display: "flex",
                gap: theme.spacePx.sp01,
                marginBottom: 0,
                paddingTop: "28px",
            },
            newActionContainer: {
                alignItems: "flex-end",
                alignSelf: "flex-end",
                display: "flex",
                gap: theme.spacePx.sp01,
                marginBottom: 0,
            },
            button: {
                alignItems: "center",
                display: "inline-flex",
                fontSize: 18,
                height: 40,
                justifyContent: "center",
                lineHeight: 1,
                marginBottom: 0,
                marginLeft: 0,
                paddingBottom: 0,
                paddingLeft: theme.spacePx.sp03,
                paddingRight: theme.spacePx.sp03,
                paddingTop: 0,
                verticalAlign: "middle",
            },
            searchButton: {
                backgroundColor: theme.palette.accent.dark,
                border: `1px solid ${theme.palette.accent.dark}`,
                color: theme.palette.white,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 1px 5px rgba(0,0,0,0.16)",
                minWidth: 64,
            },
            clearButton: {
                backgroundColor: theme.palette.accent.dark,
                border: `1px solid ${theme.palette.accent.dark}`,
                color: theme.palette.white,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 1px 5px rgba(0,0,0,0.16)",
                minWidth: 72,
            },
            newButton: {
                alignItems: "center",
                backgroundColor: theme.palette.background.main,
                border: `1px solid ${theme.palette.accent.dark}`,
                color: theme.palette.accent.dark,
                display: "inline-flex",
                height: 40,
                justifyContent: "center",
                lineHeight: 1,
                minWidth: 72,
                paddingBottom: 0,
                paddingLeft: theme.spacePx.sp03,
                paddingRight: theme.spacePx.sp03,
                paddingTop: 0,
            },
            icon: {
                fontSize: 22,
                lineHeight: 1,
            },
        },
        searchColumn: {
            marginLeft: 0,
            minWidth: 220,
        },
        searchControl: {
            minHeight: 40,
        },
        searchControlStyles: {
            label: {
                color: theme.palette.text.main,
                display: "block",
                fontSize: 13,
                lineHeight: 1.25,
                marginBottom: theme.spacePx.sp01,
            },
        },
    };

    return styles;
};

export default useServiceRequestStyles;